import type { ReactNode } from 'react'

export type FightResultOutcome = 'player' | 'enemy' | 'draw'

export type FightResultPresentation = {
  eyebrow: string
  title: string
  subtitle: string
  quantumIntensity: number
}

export function deriveFightResultPresentation(
  outcome: FightResultOutcome,
  runLabel?: string | null,
  conquered = false,
): FightResultPresentation {
  if (outcome === 'draw') {
    return {
      eyebrow: 'COMBAT RESOLUTION // EQUILIBRIUM',
      title: 'NO ASCENDANT',
      subtitle: 'THE RIFT REMAINS UNCLAIMED',
      quantumIntensity: .58,
    }
  }
  if (conquered) {
    return {
      eyebrow: `${runLabel ?? 'RIFT'} // COMPLETE`,
      title: 'ASCENSION COMPLETE',
      subtitle: 'EVERY RIVAL IN THIS RUN HAS FALLEN',
      quantumIntensity: 1,
    }
  }
  if (outcome === 'player') {
    return {
      eyebrow: runLabel ? `${runLabel} // STAGE SECURED` : 'MATCH CORE // VICTOR CONFIRMED',
      title: 'VICTORY',
      subtitle: 'COMBAT AUTHORITY ACKNOWLEDGED',
      quantumIntensity: .9,
    }
  }
  return {
    eyebrow: runLabel ? `${runLabel} // RESISTANCE HOLDS` : 'MATCH CORE // SIGNAL LOST',
    title: 'DEFEAT',
    subtitle: 'THE RIFT DEMANDS ANOTHER ASCENT',
    quantumIntensity: .74,
  }
}

export function FightResultCinematic({
  outcome,
  winnerName,
  playerName,
  enemyName,
  arenaName,
  playerAccent,
  enemyAccent,
  score,
  runLabel,
  conquered = false,
  detail,
  children,
}: {
  outcome: FightResultOutcome
  winnerName: string
  playerName: string
  enemyName: string
  arenaName: string
  playerAccent: string
  enemyAccent: string
  score: string
  runLabel?: string | null
  conquered?: boolean
  detail: string
  children: ReactNode
}) {
  const meta = deriveFightResultPresentation(outcome, runLabel, conquered)
  const accent = outcome === 'enemy' ? enemyAccent : playerAccent

  return (
    <section
      className={`fight-result-cinematic fight-result-cinematic--${outcome}`}
      data-testid="fight-result-cinematic"
      data-outcome={outcome}
      data-quantum-intensity={meta.quantumIntensity}
      style={{
        ['--result-accent' as string]: accent,
        ['--result-secondary' as string]: outcome === 'enemy' ? playerAccent : enemyAccent,
        ['--result-quantum' as string]: meta.quantumIntensity,
      }}
      aria-label="Post match actions"
    >
      <div className="fight-result-cinematic__vacuum" aria-hidden="true"><i/><i/><i/><i/><i/></div>
      <div className="fight-result-cinematic__flare" aria-hidden="true" />
      <div className="fight-result-cinematic__bar fight-result-cinematic__bar--top" aria-hidden="true" />
      <div className="fight-result-cinematic__bar fight-result-cinematic__bar--bottom" aria-hidden="true" />

      <div className="fight-result-cinematic__panel">
        <span className="fight-result-cinematic__eyebrow">{meta.eyebrow}</span>
        <h2>{meta.title}</h2>
        <p className="fight-result-cinematic__subtitle">{meta.subtitle}</p>

        <div className="fight-result-cinematic__winner">
          <small>{outcome === 'draw' ? 'BALANCE STATE' : 'ASCENDANT SIGNAL'}</small>
          <strong>{winnerName}</strong>
        </div>

        <div className="fight-result-cinematic__telemetry">
          <div><span>DUEL</span><strong>{playerName} <b>VS</b> {enemyName}</strong></div>
          <div><span>ARENA</span><strong>{arenaName}</strong></div>
          <div><span>SCORE</span><strong>{score}</strong></div>
        </div>

        <div className="fight-result-cinematic__actions">{children}</div>
        <small className="fight-result-cinematic__detail">{detail}</small>
      </div>
    </section>
  )
}
